// Language-aware localizer for our static data files.
//
// Convention: FR translations live in `/app/frontend/src/data/fr-overlays.js`
// as slug-keyed maps (SERVICES_FR, CONDITIONS_FR, ARTICLES_FR, FAQS_FR,
// PREP_GUIDES_FR, BOWEL_PREP_GUIDES_FR). The `localize()` helper below merges
// them at runtime so components can consume a single localized object.
//
// CLINICAL REVIEW NEEDED: Any translated medical/clinical fields (dose amounts,
// medications, contraindications, warnings) MUST be validated by a bilingual
// medical professional before publication. See `/app/CLINICAL_REVIEW.md`.

import { useTranslation } from "react-i18next";
import {
    SERVICES_FR,
    CONDITIONS_FR,
    ARTICLES_FR,
    FAQS_FR,
    PREP_GUIDES_FR,
    BOWEL_PREP_GUIDES_FR,
} from "@/data/fr-overlays";

function resolveLang(lang) {
    const l = (lang || "en").toString().toLowerCase();
    return l.startsWith("fr") ? "fr" : "en";
}

// Find the right FR overlay by inspecting the shape of the item.
function lookupOverlay(item) {
    if (!item || !item.slug) return null;
    const s = item.slug;
    if (SERVICES_FR[s]) return SERVICES_FR[s];
    if (CONDITIONS_FR[s]) return CONDITIONS_FR[s];
    if (ARTICLES_FR[s]) return ARTICLES_FR[s];
    if (PREP_GUIDES_FR[s]) return PREP_GUIDES_FR[s];
    if (BOWEL_PREP_GUIDES_FR[s]) return BOWEL_PREP_GUIDES_FR[s];
    return null;
}

// FAQs are keyed by question text (they have no slug).
function lookupFaqOverlay(item) {
    if (!item || !item.q) return null;
    return FAQS_FR[item.q] || null;
}

/** Merge English + French for a single data object. */
export function localize(item, lang) {
    if (!item || typeof item !== "object") return item;
    const l = resolveLang(lang);
    if (l === "en") {
        // eslint-disable-next-line no-unused-vars
        const { fr, ...rest } = item;
        return rest;
    }
    // Prefer inline `item.fr` first, then module overlay, then FAQ overlay
    const inline = item.fr || null;
    const module = lookupOverlay(item);
    const faq = lookupFaqOverlay(item);
    // eslint-disable-next-line no-unused-vars
    const { fr, ...enOnly } = item;
    return { ...enOnly, ...(module || {}), ...(faq || {}), ...(inline || {}) };
}

/** Localize a whole list. */
export function localizeList(list, lang) {
    if (!Array.isArray(list)) return list;
    return list.map((x) => localize(x, lang));
}

/** React hook: returns a memoized localizer for the current language. */
export function useLocalize() {
    const { i18n } = useTranslation();
    const lang = (i18n.resolvedLanguage || i18n.language || "en");
    return {
        lang: resolveLang(lang),
        localize: (item) => localize(item, lang),
        localizeList: (list) => localizeList(list, lang),
    };
}

export function localizeStep(step, lang) {
    return localize(step, lang);
}
